import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import { listOnAmazon, getAppConfig } from "../../../../services/apiV2";
import { DecimalInput } from "../../../../components";
import EastIcon from "@mui/icons-material/East";
import ErrorIcon from "@mui/icons-material/Error";
import { useAppSelector } from "../../../../store/hooks";
import { selectAmazonAccount } from "../../../../features/user/user.selector";
import { selectRestrictions } from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { ReactComponent as AmazonConnectIcon } from "../../../../assets/amazon_connect.svg";
import { ReactComponent as QuickListErrorIcon } from "../../../../assets/quick_list_error.svg";
import { selectExtension } from "../../../../features/extension/extension.selector";

type QuickListModalProps = {
  open: boolean;
  onClose: () => void;
  asin: string;
  imageURL: string;
  title: string;
  defaultPrice: number;
};

type ModalState =
  | "form"
  | "loading"
  | "success"
  | "error"
  | "unauthorized"
  | "restricted";

const QuickListModal = ({
  open,
  onClose,
  asin,
  imageURL,
  title,
  defaultPrice,
}: QuickListModalProps) => {
  const { isConnected } = useAppSelector(selectAmazonAccount);
  const { restrictions } = useAppSelector(selectRestrictions);
  const location = useLocation();
  const [price, setPrice] = useState<string>(String(defaultPrice ?? ""));
  const [quantity, setQuantity] = useState<string>("1");
  const [modalState, setModalState] = useState<ModalState>("form");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [restrictionReasons, setRestrictionReason] = useState<
    {
      reasonCode: string;
      message: string;
      links?: {
        resource?: string;
        title?: string;
        type?: string;
      }[];
    }[]
  >([]);
  const { isExtension } = useAppSelector(selectExtension);

  useEffect(() => {
    if (!open) return;
    if (!isConnected) {
      setModalState("unauthorized");
    } else if (
      (restrictions?.restrictionResult?.restrictions?.length ?? 0) > 0
    ) {
      const reasons =
        restrictions!.restrictionResult!.restrictions![0]?.reasons ?? [];
      setRestrictionReason(reasons);
      setModalState("restricted");
    } else {
      setModalState("form");
    }
  }, [open, isConnected, restrictions]);

  const handleClose = () => {
    onClose();
  };

  const handleExited = () => {
    setModalState("form");
    setPrice(String(defaultPrice ?? ""));
    setQuantity("1");
    setRestrictionReason([]);
    setErrorMessage("");
  };

  const handleCreateListing = () => {
    setModalState("loading");
    listOnAmazon({
      asin,
      quantity: Number(quantity),
      price: Number(price),
    })
      .then((response) => {
        const data = response?.data || response;
        if (data?.listingResult) {
          setModalState("success");
        } else if (data?.restrictionResult) {
          const reasons = data.restrictionResult?.restrictions?.[0]?.reasons;
          setRestrictionReason(reasons ?? null);
          setModalState("restricted");
        } else {
          setModalState("success");
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.ErrorMessage ?? "";
        setErrorMessage(msg);
        setModalState("error");
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            border: "1px solid #D4D6D8",
            backgroundColor: "#F9FAFB",
            maxWidth: "450px",
          },
        },
                transition: {
          onExited: handleExited,
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: "4px",
          right: "8px",
          color: "#565D6D",
          fontSize: "12px",
          fontWeight: 700,
          lineHeight: "20px",
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent
        sx={{ "&.MuiDialogContent-root": { px: "16px", mt: "20px" } }}
      >
        {modalState === "form" && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={1} alignItems="center">
              <Box
                component="img"
                src={imageURL}
                alt={title}
                sx={{
                  width: 36,
                  height: 36,
                  objectFit: "contain",
                  borderRadius: "2px",
                  border: "1px solid #E5E7EB",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#0F1111",
                  lineHeight: "18px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {title}
              </Typography>
            </Box>
            <Divider
              sx={{
                height: "1px",
                backgroundColor: "#E5E7EB",
                my: 0,
              }}
            />
            <Box display="flex" flexDirection="column" gap="8px">
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#0F1111",
                }}
              >
                Selling Price (USD)
              </Typography>
              <DecimalInput
                prefix="$"
                value={price}
                onEditing={(val) => setPrice(String(val))}
                size="md"
                align="left"
                // sx={{
                //   "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                //   "& .MuiOutlinedInput-notchedOutline": {
                //     borderColor: "#D0D5DD",
                //   },
                //   "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                //     { borderColor: "#D0D5DD" },
                //   "& input": {
                //     fontSize: "14px",
                //     fontWeight: 400,
                //     lineHeight: "20px",
                //     color: "#101828",
                //   },
                // }}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap="8px">
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#0F1111",
                }}
              >
                Quantity
              </Typography>
              <DecimalInput
                value={quantity}
                onEditing={(val) => setQuantity(String(val))}
                size="md"
                align="left"
                step={1}
                min={1}
                // sx={{
                //   "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                //   "& .MuiOutlinedInput-notchedOutline": {
                //     borderColor: "#D0D5DD",
                //   },
                //   "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                //     { borderColor: "#D0D5DD" },
                //   "& input": {
                //     fontSize: "14px",
                //     fontWeight: 400,
                //     lineHeight: "20px",
                //     color: "#101828",
                //   },
                // }}
              />
            </Box>
          </Box>
        )}

        {modalState === "loading" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <CircularProgress sx={{ color: "#0F56CA" }} />
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                color: "#6B7280",
              }}
            >
              Listing on Amazon...
            </Typography>
          </Box>
        )}

        {modalState === "success" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <CheckCircleIcon sx={{ fontSize: 56, color: "#2ECC71" }} />
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                lineHeight: "20px",
                color: "#171A1F",
              }}
            >
              Your listing is being created.
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 400,
                color: "#565D6D",
                textAlign: "center",
                WebkitLineClamp: 2,
              }}
            >
              Amazon may take 15–20 minutes to display it in your account.
            </Typography>
          </Box>
        )}

        {modalState === "unauthorized" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <AmazonConnectIcon />
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                lineHeight: "20px",
                color: "#171A1F",
              }}
            >
              Connect Amazon Account
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#565D6D",
                fontWeight: 400,
                lineHeight: "20px",
                textAlign: "center",
              }}
            >
              You need to connect your Amazon seller account to use this
              feature.
            </Typography>
          </Box>
        )}

        {modalState === "error" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <ErrorOutlineIcon sx={{ fontSize: 56, color: "#E53935" }} />
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                lineHeight: "20px",
                color: "#171A1F",
              }}
            >
              Something went wrong
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 400,
                color: "#565D6D",
                textAlign: "center",
              }}
            >
              {errorMessage || "An error occurred while creating your listing. Please try again."}
            </Typography>
          </Box>
        )}

        {modalState === "restricted" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <QuickListErrorIcon />
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                lineHeight: "20px",
                color: "#171A1F",
              }}
            >
              {restrictionReasons?.[0]?.reasonCode}
            </Typography>
            <ul style={{ color: "#565D6D" }}>
              {restrictionReasons.map((item, index) => (
                <li key={index}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#565D6D",
                      fontWeight: 400,
                      lineHeight: "20px",
                      textAlign: "left",
                    }}
                  >
                    {item?.message}{" "}
                    {item?.links?.[0]?.resource && (
                      <Typography
                        component="span"
                        onClick={() =>
                          window.open(item.links![0].resource, "_blank")
                        }
                        sx={{
                          display: "inline",
                          fontSize: "14px",
                          color: "#2D64E3",
                          fontWeight: 400,
                          lineHeight: "20px",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {"Learn more"}
                      </Typography>
                    )}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ "&.MuiDialogActions-root": { px: "16px", pb: "16px" } }}
      >
        {modalState === "form" && (
          <>
            <Button
              onClick={handleCreateListing}
              disabled={!price || !quantity}
              variant="contained"
              fullWidth
              sx={{
                textTransform: "none",
                bgcolor: "#0F56CA",
                "&:hover": { bgcolor: "#0D47A8" },
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "20px",
                height: "40px",
                borderRadius: "10px",
              }}
            >
              Create Listing&nbsp;
              <EastIcon sx={{ fontSize: "16px" }} />
            </Button>
          </>
        )}

        {modalState === "unauthorized" && (
          <Box display="flex" flexDirection="column" gap={1} width="100%">
            <Button
              onClick={() =>
                getAppConfig().then(({ data }) => {
                  const url = data.spapiEndpoints[0]?.endpoint;
                  if (url) {
                    sessionStorage.setItem(
                      "quickListReturnPath",
                      location.pathname + location.search,
                    );
                    window.open(url, isExtension ? "_blank" : "_self")
                  }
                })
              }
              variant="contained"
              fullWidth
              sx={{
                textTransform: "none",
                bgcolor: "#0F56CA",
                "&:hover": { bgcolor: "#0D47A8" },
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                height: "40px",
                color: "#FFFFFF",
                borderRadius: "6px",
              }}
            >
              Connect Amazon Account
            </Button>
            <Button
              onClick={handleClose}
              variant="contained"
              fullWidth
              sx={{
                textTransform: "none",
                bgcolor: "#00000000",
                border: "1px solid #181D1F26",
                "&:hover": { bgcolor: "#00000014", color: "#3F4653" },
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                height: "40px",
                color: "#4A5160",
                borderRadius: "6px",
              }}
            >
              Cancel
            </Button>
          </Box>
        )}

        {modalState === "error" && (
          <Box display="flex" flexDirection="column" gap={1} width="100%">
            <Button
              onClick={handleClose}
              variant="contained"
              fullWidth
              sx={{
                textTransform: "none",
                bgcolor: "#00000000",
                border: "1px solid #181D1F26",
                "&:hover": { bgcolor: "#00000014", color: "#3F4653" },
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                height: "40px",
                color: "#4A5160",
                borderRadius: "10px",
              }}
            >
              Close
            </Button>
          </Box>
        )}

        {modalState === "restricted" && (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              bgcolor: "#0F56CA",
              "&:hover": { bgcolor: "#0D47A8" },
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "26px",
              height: "40px",
              borderRadius: "10px",
            }}
          >
            Close
          </Button>
        )}

        {modalState === "success" && (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              bgcolor: "#0F56CA",
              "&:hover": { bgcolor: "#0D47A8" },
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "26px",
              height: "40px",
              borderRadius: "10px",
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuickListModal;
